/**
 * AdminAuditLogScreen — visor del log de acciones administrativas.
 *
 * Cada acción sensible (crear/editar/borrar admin, food, exercise, payment,
 * o modificar entrenadores) se registra en `al_admin_audit_log` con quién,
 * qué, cuándo, IP y user-agent. Esto te permite responder rápido a
 * "¿quién borró X y cuándo?" sin escarbar logs del container.
 *
 * Filtros disponibles: tipo de acción, tipo de target, búsqueda libre
 * (matchea actor_email o summary). Paginado de 50 entradas por página.
 */
import { useEffect, useMemo, useState } from "react";
import { listAuditLog, type AuditLogEntry, type AuditLogPage } from "../../api/admin";
import { Icon } from "../../lib/Icon";
import { EmptyState } from "../../lib/components";
import { AdminBadge, AdminGate } from "./_shared";

const ACTION_LABELS: Record<string, { label: string; tone: string }> = {
  "admin.create":         { label: "Admin creado",      tone: "ok" },
  "admin.promote":        { label: "Admin promovido",   tone: "ok" },
  "admin.update":         { label: "Admin editado",     tone: "neutral" },
  "admin.activate":       { label: "Admin activado",    tone: "ok" },
  "admin.deactivate":     { label: "Admin deshabilitado", tone: "warn" },
  "admin.revoke":         { label: "Admin revocado",    tone: "warn" },
  "admin.reset_password": { label: "Password reseteada", tone: "warn" },
  "user.patch":           { label: "Entrenador editado", tone: "neutral" },
  "payment.create":       { label: "Pago registrado",   tone: "ok" },
  "payment.delete":       { label: "Pago eliminado",    tone: "warn" },
  "food.create":          { label: "Alimento creado",   tone: "ok" },
  "food.update":          { label: "Alimento editado",  tone: "neutral" },
  "food.delete":          { label: "Alimento eliminado", tone: "warn" },
  "exercise.create":      { label: "Ejercicio creado",  tone: "ok" },
  "exercise.update":      { label: "Ejercicio editado", tone: "neutral" },
  "exercise.delete":      { label: "Ejercicio eliminado", tone: "warn" },
};

const ACTION_OPTIONS = Object.keys(ACTION_LABELS);

const TARGET_LABELS: Record<string, string> = {
  user: "Entrenador",
  admin: "Admin",
  food: "Alimento",
  exercise: "Ejercicio",
  payment: "Pago",
};

export function AdminAuditLogScreen() {
  return (
    <AdminGate>
      <Content />
    </AdminGate>
  );
}

function Content() {
  const [data, setData] = useState<AuditLogPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [action, setAction] = useState<string>("");
  const [targetType, setTargetType] = useState<string>("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q), 300);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => { setPage(1); }, [debouncedQ, action, targetType]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await listAuditLog({
          q: debouncedQ || undefined,
          action: action || undefined,
          target_type: targetType || undefined,
          page,
          page_size: 50,
        });
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedQ, action, targetType, page]);

  const totalPages = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, Math.ceil(data.total / data.page_size));
  }, [data]);

  return (
    <div className="page al-admin">
      <header className="al-admin-header">
        <div>
          <h1>
            Registro de actividad <AdminBadge />
          </h1>
          <p className="muted">
            Acciones sensibles del panel (crear/editar/eliminar entrenadores,
            admins, alimentos, ejercicios y pagos). Lecturas y navegación no se
            registran.
          </p>
        </div>
      </header>

      <div className="al-admin-foods-toolbar">
        <div className="al-admin-foods-search">
          <Icon name="search" size={14} />
          <input
            type="text"
            placeholder="Buscar por email del actor o descripción…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q && <button className="link-btn" onClick={() => setQ("")}>limpiar</button>}
        </div>
        <select
          className="al-admin-foods-cat"
          value={action}
          onChange={(e) => setAction(e.target.value)}
          title="Filtrar por tipo de acción"
        >
          <option value="">Todas las acciones</option>
          {ACTION_OPTIONS.map((a) => (
            <option key={a} value={a}>{ACTION_LABELS[a]?.label || a}</option>
          ))}
        </select>
        <select
          className="al-admin-foods-cat"
          value={targetType}
          onChange={(e) => setTargetType(e.target.value)}
          title="Filtrar por tipo de entidad afectada"
        >
          <option value="">Todas las entidades</option>
          {Object.entries(TARGET_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <div className="tiny muted al-admin-foods-count">
          {data ? `${data.total.toLocaleString("es-CO")} acciones` : "—"}
        </div>
      </div>

      {err && (
        <EmptyState
          icon="x"
          title="Error al cargar el log"
          body={err}
          action={<button className="btn btn-secondary" onClick={() => setPage((p) => p)}>Reintentar</button>}
        />
      )}

      {!err && loading && !data && (
        <div className="muted" style={{ padding: 24 }}>Cargando…</div>
      )}

      {!err && data && data.entries.length === 0 && (
        <EmptyState
          icon="sparkle"
          title="Sin actividad"
          body="Todavía no hay acciones que coincidan con los filtros."
        />
      )}

      {!err && data && data.entries.length > 0 && (
        <>
          <div className="al-admin-audit-list">
            {data.entries.map((e) => (
              <AuditEntry key={e.id} entry={e} />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="al-admin-audit-pagination">
              <button
                className="al-admin-ex-page"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                <Icon name="arrowL" size={12} /> Anterior
              </button>
              <span className="tiny muted">
                Página {page} de {totalPages}
              </span>
              <button
                className="al-admin-ex-page"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Siguiente <Icon name="arrow" size={12} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// AuditEntry — fila del log
// ----------------------------------------------------------------------

function AuditEntry({ entry }: { entry: AuditLogEntry }) {
  const meta = ACTION_LABELS[entry.action] || { label: entry.action, tone: "neutral" };
  const targetLbl = entry.target_type ? TARGET_LABELS[entry.target_type] || entry.target_type : null;
  const date = new Date(entry.created_at);
  const dateStr = date.toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
  const timeStr = date.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" });

  const [open, setOpen] = useState(false);
  const hasDetails = entry.payload && Object.keys(entry.payload).length > 0;

  return (
    <div className={"al-admin-audit-row tone-" + meta.tone}>
      <div className="al-admin-audit-when">
        <div className="al-admin-audit-when-date">{dateStr}</div>
        <div className="al-admin-audit-when-time">{timeStr}</div>
      </div>
      <div className="al-admin-audit-body">
        <div className="al-admin-audit-action">
          <span className={"al-admin-audit-badge tone-" + meta.tone}>{meta.label}</span>
          {targetLbl && (
            <span className="al-admin-audit-target">
              {targetLbl}{entry.target_id ? ` #${entry.target_id}` : ""}
            </span>
          )}
        </div>
        <div className="al-admin-audit-summary">
          {entry.summary || <span className="muted">(sin descripción)</span>}
        </div>
        <div className="al-admin-audit-actor">
          <Icon name="user" size={11} /> {entry.actor_email}
          {entry.ip && <> · <span className="tiny muted">{entry.ip}</span></>}
        </div>
        {hasDetails && (
          <>
            <button
              type="button"
              className="al-admin-audit-toggle"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? "Ocultar detalle" : "Ver detalle"}
            </button>
            {open && (
              <pre className="al-admin-audit-payload">
                {JSON.stringify(entry.payload, null, 2)}
              </pre>
            )}
          </>
        )}
      </div>
    </div>
  );
}
